import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Badge,
  Modal,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import Particle from "../Particle";
import { useParams } from "react-router-dom";
import { getImageURL, API_BASE_URL } from "../../api";
import SEO from "../SEO";
import { useTranslation } from "react-i18next";
import {
  AiFillHeart,
  AiOutlineHeart,
  AiFillEye,
  AiOutlineCheckCircle,
} from "react-icons/ai";
import { BsArrowsFullscreen } from "react-icons/bs";
import axios from "axios";

// Markdown to HTML converter
const convertMarkdownToHTML = (markdown) => {
  if (!markdown) return "";
  let html = markdown;

  // Code blocks
  html = html.replace(
    /```([\s\S]*?)```/g,
    '<pre style="background: rgba(0,0,0,0.3); padding: 15px; border-radius: 8px; overflow-x: auto;"><code>$1</code></pre>'
  );

  // Headers
  html = html.replace(
    /^### (.+)$/gm,
    '<h3 style="color: #c770f0; margin-top: 30px; margin-bottom: 15px; font-size: 1.5rem;">$1</h3>'
  );
  html = html.replace(
    /^## (.+)$/gm,
    '<h2 style="color: #c770f0; margin-top: 35px; margin-bottom: 20px; font-size: 1.8rem;">$1</h2>'
  );
  html = html.replace(
    /^# (.+)$/gm,
    '<h1 style="color: #c770f0; margin-top: 40px; margin-bottom: 25px; font-size: 2.2rem;">$1</h1>'
  );

  // Bold and Italic
  html = html.replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>");
  html = html.replace(
    /\*\*(.+?)\*\*/g,
    '<strong style="color: #fff; font-weight: 600;">$1</strong>'
  );
  html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");

  // Inline code
  html = html.replace(
    /`([^`]+)`/g,
    '<code style="background: rgba(199, 112, 240, 0.2); padding: 3px 8px; border-radius: 4px; color: #c770f0;">$1</code>'
  );

  // Links - proper anchor tags
  html = html.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #c770f0; text-decoration: underline; font-weight: 500;">$1</a>'
  );

  // Images
  html = html.replace(
    /!\[([^\]]*)\]\(([^)]+)\)/g,
    '<img src="$2" alt="$1" style="max-width: 100%; height: auto; border-radius: 12px; margin: 25px 0;" />'
  );

  // Lists
  html = html.replace(/^\* (.+)$/gm, '<li style="margin-bottom: 8px;">$1</li>');

  // Line breaks
  html = html.replace(
    /\n\n/g,
    '</p><p style="margin-bottom: 18px; line-height: 1.8;">'
  );
  html = html.replace(/\n/g, "<br/>");

  // Wrap in paragraph
  if (!html.startsWith("<")) {
    html = '<p style="margin-bottom: 18px; line-height: 1.8;">' + html + "</p>";
  }

  return html;
};

// Date formatter
const formatDate = (dateString, lang) => {
  const date = new Date(dateString);
  const day = date.getDate();
  const monthsUz = [
    "yanvar",
    "fevral",
    "mart",
    "aprel",
    "may",
    "iyun",
    "iyul",
    "avgust",
    "sentabr",
    "oktabr",
    "noyabr",
    "dekabr",
  ];
  const monthsEn = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const year = date.getFullYear();

  if (lang === "uz") {
    return `${day}-${monthsUz[date.getMonth()]} ${year}-yil`;
  }
  return `${monthsEn[date.getMonth()]} ${day}, ${year}`;
};

// Get or create device ID
const getDeviceId = () => {
  let deviceId = localStorage.getItem("blog-device-id");
  if (!deviceId) {
    deviceId = "device-" + Math.random().toString(36).substr(2, 9) + Date.now();
    localStorage.setItem("blog-device-id", deviceId);
  }
  return deviceId;
};

function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [viewCount, setViewCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalImage, setModalImage] = useState("");
  const [toast, setToast] = useState({
    show: false,
    message: "",
    variant: "success",
  });
  const [isLiking, setIsLiking] = useState(false);
  const likeTimeoutRef = useRef(null);
  const { i18n } = useTranslation();
  const lang = i18n.language.split("-")[0];

  // Show toast helper
  const showToast = (message, variant = "success") => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast({ ...toast, show: false }), 3000);
  };

  // Fetch blog data
  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE_URL}/blogs/${slug}`);
        const blogData = res.data;

        setBlog(blogData);
        setLikeCount(blogData.likes || 0);
        setViewCount(blogData.views || 0);

        // Check if already liked
        const deviceId = getDeviceId();
        const hasLiked = blogData.likedBy?.includes(deviceId) || false;
        setLiked(hasLiked);

        // Track view (only if not already viewed)
        const hasViewed = blogData.viewedBy?.includes(deviceId) || false;
        if (!hasViewed) {
          try {
            const viewRes = await axios.post(
              `${API_BASE_URL}/blogs/${blogData._id}/view`,
              { deviceId }
            );
            if (viewRes.data.success) {
              setViewCount(viewRes.data.views);
            }
          } catch (viewErr) {
            console.error("View tracking error:", viewErr);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching blog:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchBlog();
  }, [slug]);

  // Handle like button click with debounce
  const handleLike = async () => {
    if (!blog || isLiking) return;

    // Clear any pending timeout
    if (likeTimeoutRef.current) {
      clearTimeout(likeTimeoutRef.current);
    }

    setIsLiking(true);

    // Optimistic update
    const wasLiked = liked;
    setLiked(!wasLiked);
    setLikeCount((prev) => (wasLiked ? prev - 1 : prev + 1));

    const deviceId = getDeviceId();

    try {
      const res = await axios.post(`${API_BASE_URL}/blogs/${blog._id}/like`, {
        deviceId,
      });

      if (res.data.success) {
        setLiked(res.data.liked);
        setLikeCount(res.data.likes);
        showToast(
          res.data.liked
            ? lang === "uz"
              ? "❤️ Like bosildi!"
              : "❤️ Liked!"
            : lang === "uz"
              ? "💔 Like olib tashlandi"
              : "💔 Like removed",
          "success"
        );
      }
    } catch (err) {
      // Revert optimistic update on error
      setLiked(wasLiked);
      setLikeCount((prev) => (wasLiked ? prev + 1 : prev - 1));
      console.error("Like error:", err);
      showToast(
        lang === "uz" ? "Xatolik yuz berdi!" : "An error occurred!",
        "danger"
      );
    } finally {
      // Debounce - prevent rapid clicks
      likeTimeoutRef.current = setTimeout(() => {
        setIsLiking(false);
      }, 500);
    }
  };

  // Open image in modal
  const openImageModal = (imageUrl) => {
    setModalImage(imageUrl);
    setShowModal(true);
  };

  if (loading) {
    return (
      <Container fluid className="project-section">
        <Particle />
        <div className="text-white text-center" style={{ padding: "100px" }}>
          <div
            className="spinner-border"
            style={{ color: "#c770f0" }}
            role="status"
          >
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">
            {lang === "uz" ? "Yuklanmoqda..." : "Loading..."}
          </p>
        </div>
      </Container>
    );
  }

  if (error || !blog) {
    return (
      <Container fluid className="project-section">
        <Particle />
        <div className="text-white text-center" style={{ padding: "100px" }}>
          <h2>{lang === "uz" ? "Blog topilmadi" : "Blog not found"}</h2>
          <p>{error}</p>
        </div>
      </Container>
    );
  }

  return (
    <Container
      fluid
      className="project-section"
      style={{ position: "relative", zIndex: 1 }}
    >
      <SEO
        title={blog.seoTitle?.[lang] || blog.title[lang]}
        description={
          blog.seoDescription?.[lang] ||
          blog.excerpt?.[lang] ||
          blog.content[lang].substring(0, 160)
        }
        image={getImageURL(blog.coverImage)}
        type="article"
      />
      <Particle />

      <Container
        style={{ maxWidth: "900px", position: "relative", zIndex: 10 }}
      >
        <Row className="justify-content-center">
          <Col lg={12}>
            {/* Header */}
            <div className="text-center mb-5">
              <h1
                className="purple"
                style={{
                  fontSize: "2.5rem",
                  fontWeight: "700",
                  marginBottom: "20px",
                }}
              >
                {blog.title[lang]}
              </h1>

              {blog.category?.[lang] && (
                <Badge
                  bg="purple"
                  className="mb-3 px-3 py-2"
                  style={{ fontSize: "0.9rem", background: "#c770f0" }}
                >
                  {blog.category[lang]}
                </Badge>
              )}

              {/* Stats */}
              <div
                className="d-flex justify-content-center align-items-center gap-4 mb-4 flex-wrap"
                style={{
                  padding: "15px 25px",
                  background: "rgba(199, 112, 240, 0.1)",
                  borderRadius: "12px",
                  display: "inline-flex",
                }}
              >
                <span
                  className="d-flex align-items-center gap-2"
                  style={{ color: "#fff", fontWeight: "500" }}
                >
                  <AiFillEye size={22} color="#c770f0" />
                  <strong>{viewCount}</strong>{" "}
                  {lang === "uz" ? "ko'rildi" : "views"}
                </span>
                <span
                  className="d-flex align-items-center gap-2"
                  style={{
                    color: liked ? "#c770f0" : "#fff",
                    fontWeight: "600",
                    padding: "5px 15px",
                    borderRadius: "20px",
                    background: liked
                      ? "rgba(199, 112, 240, 0.2)"
                      : "transparent",
                    border: liked ? "2px solid #c770f0" : "none",
                  }}
                >
                  {liked ? (
                    <AiFillHeart
                      size={22}
                      color="#ff4d6d"
                      style={{ filter: "drop-shadow(0 0 8px #ff4d6d)" }}
                    />
                  ) : (
                    <AiOutlineHeart size={22} color="#c770f0" />
                  )}
                  <strong>{likeCount}</strong>{" "}
                  {lang === "uz" ? "yoqdi" : "likes"}
                </span>
                <span style={{ color: "rgba(255,255,255,0.8)" }}>
                  📅 {formatDate(blog.createdAt, lang)}
                </span>
              </div>

              {/* Tags */}
              {blog.tags && blog.tags.length > 0 && (
                <div className="d-flex justify-content-center gap-2 flex-wrap mt-3">
                  {blog.tags.map((tag, idx) => (
                    <Badge
                      key={idx}
                      bg="secondary"
                      className="px-3 py-1"
                      style={{
                        background: "rgba(255,255,255,0.1)",
                        border: "1px solid rgba(199, 112, 240, 0.3)",
                      }}
                    >
                      #{tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Cover Image */}
            {blog.coverImage && (
              <div
                className="mb-5 position-relative"
                style={{ cursor: "pointer" }}
                onClick={() => openImageModal(getImageURL(blog.coverImage))}
              >
                <img
                  src={getImageURL(blog.coverImage)}
                  alt={blog.title[lang]}
                  className="img-fluid"
                  style={{
                    borderRadius: "15px",
                    width: "100%",
                    maxHeight: "450px",
                    objectFit: "cover",
                    boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
                  }}
                />
                <div
                  className="position-absolute top-0 end-0 m-3"
                  style={{
                    background: "rgba(0,0,0,0.6)",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    color: "#fff",
                  }}
                >
                  <BsArrowsFullscreen className="me-1" />
                  {lang === "uz" ? "To'liq ko'rish" : "Full View"}
                </div>
              </div>
            )}

            {/* Content */}
            <article
              className="blog-content text-white"
              style={{
                fontSize: "1.1rem",
                lineHeight: "1.9",
                background: "rgba(20, 15, 35, 0.4)",
                padding: "40px",
                borderRadius: "20px",
                border: "1px solid rgba(199, 112, 240, 0.15)",
              }}
              dangerouslySetInnerHTML={{
                __html: convertMarkdownToHTML(blog.content[lang]),
              }}
            />

            {/* Gallery */}
            {blog.images && blog.images.length > 0 && (
              <div className="mt-5">
                <h3
                  className="purple mb-4"
                  style={{ fontSize: "1.5rem", fontWeight: "600" }}
                >
                  {lang === "uz" ? "📸 Galereya" : "📸 Image Gallery"}
                </h3>
                <Row className="g-3">
                  {blog.images.map((img, idx) => (
                    <Col key={idx} xs={6} md={4} lg={3}>
                      <div
                        style={{
                          cursor: "pointer",
                          borderRadius: "12px",
                          overflow: "hidden",
                          boxShadow: "0 5px 15px rgba(0,0,0,0.3)",
                        }}
                        onClick={() => openImageModal(getImageURL(img))}
                      >
                        <img
                          src={getImageURL(img)}
                          alt={`Gallery ${idx + 1}`}
                          style={{
                            width: "100%",
                            height: "150px",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            )}

            {/* Like Section - Desktop: flex-row, Mobile: column */}
            <div
              className="mt-5 py-4 px-4"
              style={{
                background:
                  "linear-gradient(145deg, rgba(30, 20, 50, 0.9) 0%, rgba(50, 30, 80, 0.8) 100%)",
                borderRadius: "24px",
                border: "2px solid rgba(199, 112, 240, 0.5)",
                position: "relative",
                zIndex: 100,
                boxShadow:
                  "0 20px 60px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255,255,255,0.1)",
              }}
            >
              {/* Decorative glow */}
              <div
                style={{
                  position: "absolute",
                  top: "-2px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "60%",
                  height: "4px",
                  background:
                    "linear-gradient(90deg, transparent, #c770f0, transparent)",
                  borderRadius: "4px",
                }}
              />

              {/* Desktop: Row layout / Mobile: Column layout */}
              <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-4">
                {/* Left side - Text */}
                <div className="text-center text-md-start">
                  <h4
                    className="text-white mb-2"
                    style={{
                      fontSize: "1.4rem",
                      fontWeight: "700",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {lang === "uz"
                      ? "💜 Maqola yoqdimi?"
                      : "💜 Did you enjoy this article?"}
                  </h4>
                  <p
                    className="mb-0"
                    style={{
                      color: "rgba(255,255,255,0.7)",
                      fontSize: "0.95rem",
                    }}
                  >
                    {lang === "uz"
                      ? "Agar foydali bo'lsa, like bosing!"
                      : "If you found it helpful, give it a like!"}
                  </p>
                </div>

                {/* Right side - Like button and counter */}
                <div className="d-flex align-items-center gap-3">
                  <Button
                    variant={liked ? "primary" : "outline-primary"}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleLike();
                    }}
                    disabled={isLiking}
                    className="like-btn"
                    style={{
                      background: liked
                        ? "linear-gradient(135deg, #ff6b8a 0%, #c770f0 50%, #9b30ff 100%)"
                        : "transparent",
                      border: "3px solid",
                      borderColor: liked ? "#ff6b8a" : "#c770f0",
                      borderRadius: "50px",
                      padding: "12px 30px",
                      fontSize: "1.1rem",
                      fontWeight: "700",
                      color: "#fff",
                      cursor: isLiking ? "wait" : "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "10px",
                      transition:
                        "all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                      boxShadow: liked
                        ? "0 8px 30px rgba(255, 107, 138, 0.4)"
                        : "0 4px 20px rgba(199, 112, 240, 0.3)",
                      opacity: isLiking ? 0.7 : 1,
                      minWidth: "140px",
                      justifyContent: "center",
                    }}
                  >
                    {isLiking ? (
                      <span className="spinner-border spinner-border-sm" />
                    ) : liked ? (
                      <>
                        <AiFillHeart
                          size={24}
                          color="#fff"
                          style={{
                            filter: "drop-shadow(0 0 10px #ff6b8a)",
                          }}
                        />
                        <span>{lang === "uz" ? "Yoqdi!" : "Liked!"}</span>
                      </>
                    ) : (
                      <>
                        <AiOutlineHeart size={24} />
                        <span>{lang === "uz" ? "Like" : "Like"}</span>
                      </>
                    )}
                  </Button>

                  {/* Like counter badge */}
                  <div
                    className="d-flex align-items-center"
                    style={{
                      color: "#fff",
                      background: liked
                        ? "linear-gradient(135deg, rgba(255, 107, 138, 0.25) 0%, rgba(199, 112, 240, 0.25) 100%)"
                        : "rgba(199, 112, 240, 0.15)",
                      padding: "10px 20px",
                      borderRadius: "30px",
                      border: liked
                        ? "2px solid rgba(255, 107, 138, 0.5)"
                        : "1px solid rgba(199, 112, 240, 0.4)",
                      gap: "8px",
                      boxShadow: liked
                        ? "0 4px 15px rgba(255, 107, 138, 0.2)"
                        : "none",
                    }}
                  >
                    <AiFillHeart
                      size={18}
                      color={liked ? "#ff6b8a" : "#c770f0"}
                    />
                    <span
                      style={{
                        fontSize: "1.3rem",
                        fontWeight: "800",
                        color: liked ? "#ff6b8a" : "#c770f0",
                      }}
                    >
                      {likeCount}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Toast Notifications */}
      <ToastContainer
        position="top-end"
        className="p-3"
        style={{ position: "fixed", top: 20, right: 20, zIndex: 9999 }}
      >
        <Toast
          show={toast.show}
          onClose={() => setToast({ ...toast, show: false })}
          bg={toast.variant === "success" ? "dark" : "danger"}
          delay={3000}
          autohide
        >
          <Toast.Body
            className="d-flex align-items-center gap-2 text-white"
            style={{ fontSize: "1rem" }}
          >
            {toast.variant === "success" ? (
              <AiOutlineCheckCircle size={20} color="#4ade80" />
            ) : (
              <span>⚠️</span>
            )}
            {toast.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Image Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="xl"
        dialogClassName="image-modal"
      >
        <Modal.Body className="p-0 text-center" style={{ background: "#000" }}>
          <img
            src={modalImage}
            alt="Full view"
            style={{
              maxWidth: "100%",
              maxHeight: "90vh",
              objectFit: "contain",
            }}
          />
          <Button
            variant="light"
            onClick={() => setShowModal(false)}
            className="position-absolute top-0 end-0 m-3"
            style={{
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              padding: 0,
            }}
          >
            ✕
          </Button>
        </Modal.Body>
      </Modal>
    </Container>
  );
}

export default BlogDetail;
